import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Modal from '../../../../../components/modal/modal.component';
import { StyledModalContent } from '@components/modal/modal.style';
import {IonCol, IonGrid, IonLoading, IonRow, IonToast} from '@ionic/react';
import {StyledButton} from '@components/button/button.style';
import {getReport, getReportTimepickerMinutesIterations, transformApiFormatToReport, saveBulkReportWorkerHours, BulkReportWorkerHours} from '@services/report.service';
import TimeInput from '../../../../../components/form/input/time.component';
import {Report as ReportListItem} from "../../../../../models/raport";
import {Report, ReportRow, ReportRowWorker, Order} from "@app/reportPage/reportPage.component";
import styled from 'styled-components';
import ArrowRightImage from '../../../../../assets/images/arrow-right.svg';
import AvatarImage from '../../../../../assets/images/logo/x-gray.png';

const MomentRange = require('moment-range');
const Moment = require('moment-timezone');
const moment = MomentRange.extendMoment(Moment);
moment.tz.setDefault('Europe/Warsaw');

type BulkReportsManagementModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    reports: ReportListItem[];
};

interface ReportDetails {
    reportListItem: ReportListItem;
    reportData: Report | null;
    order: Order | null;
}

interface WorkerTimeConflict {
    reportIndex: number;
    rowIndex: number;
    workerIndex: number;
    workerNumber: string;
    from: string;
    to: string;
}

const BulkReportsManagementModal: React.FC<BulkReportsManagementModalProps> = (props: BulkReportsManagementModalProps) => {
    const {t} = useTranslation();

    const [reportsDetails, setReportsDetails] = useState<ReportDetails[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [toast, setToast] = useState<any>('');
    const [timeErrors, setTimeErrors] = useState<Map<string, 'overlap' | 'invalid_range'>>(new Map());

    useEffect(() => {
        if (props.isOpen && props.reports.length > 0) {
            loadReportsDetails();
        }
    }, [props.isOpen, props.reports]);

    useEffect(() => {
        if (reportsDetails.length > 0) {
            validateTimeConflicts();
        }
    }, [reportsDetails]);

    const loadReportsDetails = async () => {
        setLoading(true);
        const details: ReportDetails[] = [];

        for (const report of props.reports) {
            try {
                const response = await getReport(report.id.toString());

                if (!response || !response.data) {
                    throw new Error('No response data');
                }

                const reportData = transformApiFormatToReport(response.data);
                const order = response.data.order as Order;

                details.push({
                    reportListItem: report,
                    reportData,
                    order
                });
            } catch (error) {
                console.error('Error loading report:', error);
                details.push({
                    reportListItem: report,
                    reportData: null,
                    order: null
                });
            }
        }

        setReportsDetails(details);
        setLoading(false);
    };

    const validateTimeConflicts = () => {
        const errorMap = new Map<string, 'overlap' | 'invalid_range'>();
        const workerTimes: { [key: string]: WorkerTimeConflict[] } = {};

        // Zgromadź wszystkie czasy pracy i sprawdź poprawność zakresów
        reportsDetails.forEach((detail, reportIndex) => {
            if (!detail.reportData || !detail.order) return;

            const date = moment(detail.reportListItem.service_date, moment.ISO_8601).format('YYYY-MM-DD');
            const workingDayEnd = detail.order.working_day_end || '0600';

            detail.reportData.rows.forEach((row, rowIndex) => {
                row.workers.forEach((worker, workerIndex) => {
                    if (!worker.from || !worker.to || !worker.number) return;

                    const errorKey = `${reportIndex}-${rowIndex}-${workerIndex}`;

                    // Pomiń sprawdzanie nakładania dla pracowników wirtualnych
                    const isVirtualWorker = worker.virtualWorker === true;

                    // Walidacja zakresu godzin (taka sama jak w extendedWorkerModal)
                    const from = moment(worker.from, moment.ISO_8601).format('HHmm');
                    const to = moment(worker.to, moment.ISO_8601).format('HHmm');

                    // Błąd jeśli from = to (czas pracy = 0)
                    if (from === to) {
                        errorMap.set(errorKey, 'invalid_range');
                    }
                    // Jeśli from > to (zmiana nocna)
                    else if (from > to) {
                        // Błąd jeśli to > workingDayEnd (np. 8:00 do 7:00)
                        if (to > workingDayEnd) {
                            errorMap.set(errorKey, 'invalid_range');
                        }
                        // Jeśli to <= workingDayEnd → OK (np. 7:00 do 5:00)
                    }
                    // Jeśli from < to (zwykły zakres)
                    else if (from < to) {
                        // Błąd jeśli przekracza granicę doby niepoprawnie
                        if (from < workingDayEnd && to > workingDayEnd) {
                            errorMap.set(errorKey, 'invalid_range');
                        }
                    }

                    // Dodaj do listy czasów dla sprawdzenia nakładania (pomijając pracowników wirtualnych)
                    if (!errorMap.has(errorKey) && !isVirtualWorker) {
                        const key = `${worker.number}_${date}`;
                        workerTimes[key] = workerTimes[key] || [];

                        workerTimes[key].push({
                            reportIndex,
                            rowIndex,
                            workerIndex,
                            workerNumber: worker.number,
                            from: worker.from,
                            to: worker.to
                        });
                    }
                });
            });
        });

        // Sprawdź nakładające się czasy
        Object.entries(workerTimes).forEach(([key, times]) => {
            if (times.length < 2) return;

            for (let i = 0; i < times.length; i++) {
                for (let j = i + 1; j < times.length; j++) {
                    const time1 = times[i];
                    const time2 = times[j];

                    const from1 = moment(time1.from, moment.ISO_8601);
                    const to1 = moment(time1.to, moment.ISO_8601);
                    const from2 = moment(time2.from, moment.ISO_8601);
                    const to2 = moment(time2.to, moment.ISO_8601);

                    // Przedziały nakładają się jeśli: from1 < to2 AND from2 < to1
                    const overlaps = from1.isBefore(to2) && from2.isBefore(to1);

                    if (overlaps) {
                        const key1 = `${time1.reportIndex}-${time1.rowIndex}-${time1.workerIndex}`;
                        const key2 = `${time2.reportIndex}-${time2.rowIndex}-${time2.workerIndex}`;
                        errorMap.set(key1, 'overlap');
                        errorMap.set(key2, 'overlap');
                    }
                }
            }
        });

        setTimeErrors(errorMap);

        // Wyświetl alert jeśli są błędy
        if (errorMap.size > 0) {
            const hasOverlaps = Array.from(errorMap.values()).some(v => v === 'overlap');
            const hasInvalidRanges = Array.from(errorMap.values()).some(v => v === 'invalid_range');

            if (hasInvalidRanges) {
                setToast({
                    message: t('reportsTab.bulkManagement.invalidTimeRanges'),
                    type: 'danger'
                });
            } else if (hasOverlaps) {
                setToast({
                    message: t('reportsTab.bulkManagement.timeConflicts'),
                    type: 'danger'
                });
            }
        }
    };

    const updateWorkerTime = (reportIndex: number, rowIndex: number, workerIndex: number, field: 'from' | 'to', value: string) => {
        const updatedDetails = [...reportsDetails];
        if (updatedDetails[reportIndex].reportData) {
            const worker = updatedDetails[reportIndex].reportData!.rows[rowIndex].workers[workerIndex];
            if (field === 'from') {
                worker.from = value;
            } else {
                worker.to = value;
            }

            setReportsDetails(updatedDetails);
        }
    };

    const saveAllChanges = async () => {
        // Sprawdź czy są błędy przed zapisem
        if (timeErrors.size > 0) {
            const hasInvalidRanges = Array.from(timeErrors.values()).some(v => v === 'invalid_range');
            setToast({
                message: hasInvalidRanges
                    ? t('reportsTab.bulkManagement.cannotSaveWithInvalidRanges')
                    : t('reportsTab.bulkManagement.cannotSaveWithConflicts'),
                type: 'danger'
            });
            return;
        }

        setSaving(true);

        try {
            // Przygotuj dane dla bulk save endpoint
            const bulkData: BulkReportWorkerHours[] = [];

            reportsDetails.forEach((detail) => {
                if (!detail.reportData || !detail.reportData.rows) return;

                const workers: BulkReportWorkerHours['workers'] = [];

                detail.reportData.rows.forEach((row, rowIndex) => {
                    row.workers.forEach((worker, workerIndex) => {
                        if (worker.from && worker.to && worker.number) {
                            workers.push({
                                row_index: rowIndex,
                                worker_index: workerIndex,
                                worker_number: worker.number,
                                from: worker.from,
                                to: worker.to
                            });
                        }
                    });
                });

                if (workers.length > 0) {
                    bulkData.push({
                        report_id: Number(detail.reportListItem.id),
                        workers
                    });
                }
            });

            // Wyślij wszystkie zmiany jednym requestem
            await saveBulkReportWorkerHours(bulkData);

            setSaving(false);
            setToast({
                message: t('reportsTab.bulkManagement.saveSuccess'),
                type: 'success'
            });
            props.onSuccess();
            props.onClose();
        } catch (error) {
            console.error('Error saving bulk reports:', error);
            setSaving(false);

            const responseError = (error as any)?.response;
            let errorMessage = t('reportsTab.bulkManagement.saveError');

            if (responseError) {
                const status = responseError.status;
                const data = responseError.data;

                // Obsługa różnych statusów błędów
                if (status === 403) {
                    errorMessage = t("reportPage.validation.reportLocked");
                } else if (status >= 500) {
                    errorMessage = t("common.serverErrorMsg");
                } else if (status === 400 && data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
                    // Parsuj i tłumacz błędy walidacji
                    const translatedErrors = data.errors.map((err: string) => {
                        if (err.includes('overlapping hours with reports:')) {
                            const parts = err.split('overlapping hours with reports:');
                            const workerName = parts[0].replace('Worker', '').trim();
                            const reports = parts[1]?.trim();
                            return t('reportPage.validation.workerReportAlreadyExists', {
                                worker: workerName || '',
                                reports: reports || ''
                            });
                        }
                        return err;
                    });
                    errorMessage = translatedErrors.join('\n');
                } else if (data?.message) {
                    errorMessage = data.message;
                }
            } else if ((error as any)?.message) {
                errorMessage = (error as any).message;
            }

            setToast({
                message: errorMessage,
                type: 'danger'
            });
        }
    };

    return (
        <>
            <Modal
                isOpen={props.isOpen}
                onDidDismiss={props.onClose}
                backdropDismiss={!saving}
            >
                <StyledModalContent>
                    <div className="content full-height" style={{overflowX: 'hidden'}}>
                        <h1 className="title center">{t('reportsTab.bulkManagement.title')}</h1>
                        <StyledBulkReportsContent>
                            {!loading && reportsDetails.length === 0 && (
                                <div className="no-reports">{t('reportsTab.bulkManagement.noReports')}</div>
                            )}
                            {!loading && reportsDetails
                                .filter(detail => {
                                    // Filtruj tylko raporty które mają pracowników
                                    if (!detail.reportData || !detail.reportData.rows) return false;
                                    const hasWorkers = detail.reportData.rows.some(row => row.workers && row.workers.length > 0);
                                    return hasWorkers;
                                })
                                .map((detail, filteredIndex, filteredArray) => {
                                    // Znajdź oryginalny indeks w niefiltrowanej liście
                                    const originalReportIndex = reportsDetails.indexOf(detail);

                                    const date = moment(detail.reportListItem.service_date, moment.ISO_8601).tz('Europe/Warsaw');
                                    const previous = filteredIndex > 0 ? filteredArray[filteredIndex - 1] : null;
                                    const prevDate = previous && previous.reportListItem.service_date
                                        ? moment(previous.reportListItem.service_date, moment.ISO_8601).tz('Europe/Warsaw')
                                        : null;

                                    const sameDate = prevDate && date.format('DD.MM.YYYY') === prevDate.format('DD.MM.YYYY');

                                    return (
                                        <React.Fragment key={originalReportIndex}>
                                            {!sameDate && (
                                                <div className="date-separator">
                                                    <div className="date-box">{date.format('DD.MM.YYYY')}</div>
                                                </div>
                                            )}
                                            <div className="report-section">
                                                <div className="report-header">
                                                    {t('reportsTab.orders.report')} {detail.reportListItem.formatted_number}
                                                </div>
                                                {detail.reportData && (
                                                    <IonGrid className="ion-no-padding">
                                                        {detail.reportData.rows.map((row: ReportRow, rowIndex: number) => (
                                                            <div key={rowIndex}>
                                                                {row.workers.map((worker: ReportRowWorker, workerIndex: number) => {
                                                                    const errorKey = `${originalReportIndex}-${rowIndex}-${workerIndex}`;
                                                                    const hasError = timeErrors.has(errorKey);

                                                                    return (
                                                                        <IonRow
                                                                            key={'worker_' + originalReportIndex + '_' + rowIndex + '_' + workerIndex}
                                                                            className="worker-row"
                                                                        >
                                                                            <IonCol size="2">
                                                                                <img className="avatar" src={worker.avatarUrl || AvatarImage}/>
                                                                            </IonCol>
                                                                            <IonCol size="10" className="label name">
                                                                                {worker.name}
                                                                            </IonCol>
                                                                            <IonCol size="5.5">
                                                                                <TimeInput
                                                                                    keepContentsMounted={true}
                                                                                    cancelText={t('common.dateCancel')}
                                                                                    doneText={t('common.dateDone')}
                                                                                    placeholder={t('common.type')}
                                                                                    className={hasError ? 'hasErrors' : ''}
                                                                                    value={worker.from}
                                                                                    onChange={(value: string) => {
                                                                                        updateWorkerTime(originalReportIndex, rowIndex, workerIndex, 'from', value);
                                                                                    }}
                                                                                    minuteValues={getReportTimepickerMinutesIterations(detail.order)}
                                                                                />
                                                                            </IonCol>
                                                                            <IonCol className="center" size="1">
                                                                                <img src={ArrowRightImage} alt="arrow"/>
                                                                            </IonCol>
                                                                            <IonCol size="5.5">
                                                                                <TimeInput
                                                                                    keepContentsMounted={true}
                                                                                    cancelText={t('common.dateCancel')}
                                                                                    doneText={t('common.dateDone')}
                                                                                    placeholder={t('common.type')}
                                                                                    className={hasError ? 'hasErrors' : ''}
                                                                                    value={worker.to}
                                                                                    onChange={(value: string) => {
                                                                                        updateWorkerTime(originalReportIndex, rowIndex, workerIndex, 'to', value);
                                                                                    }}
                                                                                    minuteValues={getReportTimepickerMinutesIterations(detail.order)}
                                                                                />
                                                                            </IonCol>
                                                                        </IonRow>
                                                                    );
                                                                })}
                                                            </div>
                                                        ))}
                                                    </IonGrid>
                                                )}
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                        </StyledBulkReportsContent>
                        <div style={{padding: '16px', paddingBottom: '40px'}}>
                            <StyledButton
                                onClick={saveAllChanges}
                                disabled={saving || loading || reportsDetails.length === 0 || timeErrors.size > 0}
                            >
                                {saving ? t('common.saving') : t('common.save')}
                            </StyledButton>
                        </div>
                    </div>
                    <div className="footer">
                        {/* Button już jest w content, więc footer pusty */}
                    </div>
                </StyledModalContent>
            </Modal>

            <IonLoading
                isOpen={loading}
                message={t('common.loading')}
            />

            <IonToast
                isOpen={!!toast}
                onDidDismiss={() => setToast('')}
                message={toast?.message}
                duration={3000}
                position="top"
                color={toast?.type}
            />
        </>
    );
};

const StyledBulkReportsContent = styled.div`
    .no-reports {
        padding: 20px;
        text-align: center;
        font-size: 14px;
        color: #888A98;
    }

    .date-separator {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        margin: 27px 0 20px 0;

        .date-box {
            background: #FFFFFF;
            border: 1px solid #F6F6F8;
            box-sizing: border-box;
            border-radius: 39px;
            padding: 3px 8px;
            font-size: 11px;
            line-height: 140%;
            color: #888A98;
            position: relative;
            z-index: 2;
        }

        &::before {
            content: '';
            width: 200vw;
            border: 1px solid #F6F6F8;
            position: absolute;
            left: -50vw;
        }
    }

    .report-section {
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid #EDEDF3;

        &:last-child {
            border-bottom: none;
        }
    }

    .report-header {
        font-weight: 600;
        font-size: 14px;
        line-height: 140%;
        color: #000000;
        margin-bottom: 12px;
        text-align: center;
    }

    .worker-row {
        padding: 12px 0;
        border-top: 1px solid #F5F5F5;

        .avatar {
            width: 36px;
            min-width: 36px;
            height: 36px;
            min-height: 36px;
            border-radius: 28px;
        }

        .label {
            display: flex;
            align-items: center;
            font-weight: 500;
            font-size: 14px;
            line-height: 140%;
            color: #000000;

            &.name {
                padding-left: 8px;
            }
        }

        .center {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }
`;

export default BulkReportsManagementModal;
